import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../services/firebase';
import { signOut } from 'firebase/auth';
import { 
  doc, 
  getDoc, 
  setDoc, 
  collection, 
  query, 
  orderBy, 
  getDocs,
  addDoc,
  deleteDoc,
  updateDoc
} from 'firebase/firestore';

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  
  // Profile data
  const [profile, setProfile] = useState({
    slug: '',
    displayName: '',
    bio: '',
    email: '',
    phone: '',
    avatarUrl: '',
    glowColor: '#d4af37',
    glowIntensity: 0.4,
    cardFont: 'playfair' // playfair, inter, montserrat, bebas, cinzel
  });

  // Links data
  const [links, setLinks] = useState([]);
  const [newLink, setNewLink] = useState({ title: '', url: '' });

  // Load user and profile data
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      setUser(currentUser);

      // Check subscription status
      await checkSubscription(currentUser.uid);

      // Check for pending slug from signup
      const pendingSlug = sessionStorage.getItem('pendingSlug');
      const pendingUid = sessionStorage.getItem('pendingUid');

      if (pendingSlug && pendingUid === currentUser.uid) {
        // Create initial profile
        const profileRef = doc(db, 'profiles', pendingSlug);
        await setDoc(profileRef, {
          ownerUid: currentUser.uid,
          displayName: '',
          bio: '',
          email: '',
          phone: '',
          avatarUrl: '',
          glowColor: '#d4af37',
          glowIntensity: 0.4,
          cardFont: 'playfair'
        });
        
        setProfile(prev => ({ ...prev, slug: pendingSlug }));
        sessionStorage.removeItem('pendingSlug');
        sessionStorage.removeItem('pendingUid');
      } else {
        // Load existing profile
        await loadProfile(currentUser.uid);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Check subscription status
  const checkSubscription = async (uid) => {
    try {
      const subRef = doc(db, 'subscriptions', uid);
      const subDoc = await getDoc(subRef);
      
      if (subDoc.exists()) {
        const subData = subDoc.data();
        setHasActiveSubscription(subData.status === 'active');
      } else {
        // No subscription doc = check if coming from payment
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('payment') === 'success') {
          // Just paid - create subscription record
          await setDoc(subRef, {
            status: 'active',
            createdAt: new Date(),
            expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days
          });
          setHasActiveSubscription(true);
        } else {
          setHasActiveSubscription(false);
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
      setHasActiveSubscription(false);
    }
  };

  // Load profile from Firestore
  const loadProfile = async (uid) => {
    try {
      // Find profile by ownerUid
      const profilesRef = collection(db, 'profiles');
      const snapshot = await getDocs(profilesRef);
      
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        if (data.ownerUid === uid) {
          setProfile({
            slug: docSnap.id,
            displayName: data.displayName || '',
            bio: data.bio || '',
            email: data.email || '',
            phone: data.phone || '',
            avatarUrl: data.avatarUrl || '',
            glowColor: data.glowColor || '#d4af37',
            glowIntensity: data.glowIntensity || 0.4,
            cardFont: data.cardFont || 'playfair'
          });

          // Load links
          await loadLinks(docSnap.id);
          return;
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  // Load links from Firestore
  const loadLinks = async (slug) => {
    try {
      const linksRef = collection(db, 'profiles', slug, 'links');
      const q = query(linksRef, orderBy('order', 'asc'));
      const snapshot = await getDocs(q);
      
      const loadedLinks = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setLinks(loadedLinks);
    } catch (error) {
      console.error('Error loading links:', error);
    }
  };

  // Save profile changes
  const handleProfileChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const saveProfile = async () => {
    if (!profile.slug) return;

    try {
      const profileRef = doc(db, 'profiles', profile.slug);
      await setDoc(profileRef, {
        ownerUid: user.uid,
        displayName: profile.displayName,
        bio: profile.bio,
        email: profile.email,
        phone: profile.phone,
        avatarUrl: profile.avatarUrl,
        glowColor: profile.glowColor,
        glowIntensity: profile.glowIntensity,
        cardFont: profile.cardFont
      });
      
      alert('Profile saved successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Error saving profile. Please try again.');
    }
  };

  // Add new link
  const handleAddLink = async () => {
    if (!newLink.title || !newLink.url || !profile.slug) return;

    try {
      const linksRef = collection(db, 'profiles', profile.slug, 'links');
      await addDoc(linksRef, {
        title: newLink.title,
        url: newLink.url,
        order: links.length
      });

      setNewLink({ title: '', url: '' });
      await loadLinks(profile.slug);
    } catch (error) {
      console.error('Error adding link:', error);
      alert('Error adding link. Please try again.');
    }
  };

  // Delete link
  const handleDeleteLink = async (linkId) => {
    if (!profile.slug) return;

    try {
      const linkRef = doc(db, 'profiles', profile.slug, 'links', linkId);
      await deleteDoc(linkRef);
      await loadLinks(profile.slug);
    } catch (error) {
      console.error('Error deleting link:', error);
      alert('Error deleting link. Please try again.');
    }
  };

  // Move link up/down
  const handleMoveLink = async (index, direction) => {
    if (!profile.slug) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= links.length) return;

    const newLinks = [...links];
    [newLinks[index], newLinks[newIndex]] = [newLinks[newIndex], newLinks[index]];

    // Update order in Firestore
    try {
      for (let i = 0; i < newLinks.length; i++) {
        const linkRef = doc(db, 'profiles', profile.slug, 'links', newLinks[i].id);
        await updateDoc(linkRef, { order: i });
      }
      
      setLinks(newLinks);
    } catch (error) {
      console.error('Error reordering links:', error);
      alert('Error reordering links. Please try again.');
    }
  };

  // Logout
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-page" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <div className="loading-spinner" style={{ 
          width: '40px', 
          height: '40px',
          borderWidth: '4px'
        }}></div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        {/* Header */}
        <header className="dashboard-header">
          <div className="dashboard-logo">
            <span className="dashboard-logo-icon">R</span>
            <span>RiseMyRank</span>
          </div>
          <div className="dashboard-header-actions">
            <div className="dashboard-slug-display">
              risemyrank.club/{profile.slug}
            </div>
            <button 
              onClick={saveProfile}
              className="dashboard-btn dashboard-btn-primary"
            >
              💾 Save Changes
            </button>
            <button 
              onClick={handleLogout}
              className="dashboard-btn dashboard-btn-secondary"
            >
              Logout
            </button>
          </div>
        </header>

        {/* Subscription Access Control */}
        {!hasActiveSubscription ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '70vh',
            textAlign: 'center',
            padding: 'var(--spacing-xl)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <div style={{
              width: '120px',
              height: '120px',
              background: 'linear-gradient(135deg, var(--gold), var(--gold-bright))',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '4rem',
              marginBottom: 'var(--spacing-lg)',
              boxShadow: '0 8px 32px rgba(212, 175, 55, 0.3)'
            }}>
              🔒
            </div>
            
            <h2 style={{
              fontFamily: 'var(--font-display)',
              fontSize: '2rem',
              marginBottom: 'var(--spacing-md)',
              color: 'var(--white)'
            }}>
              Premium Access Required
            </h2>
            
            <p style={{
              fontSize: '1.1rem',
              color: 'var(--white-dim)',
              marginBottom: 'var(--spacing-xl)',
              lineHeight: '1.6'
            }}>
              To access your dashboard and create your premium digital business card, 
              please complete your subscription payment.
            </p>
            
            <div style={{
              background: 'var(--grey-darkest)',
              border: '1px solid var(--grey-medium)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-lg)',
              marginBottom: 'var(--spacing-xl)',
              width: '100%'
            }}>
              <div style={{ fontSize: '0.9rem', color: 'var(--white-dim)', marginBottom: 'var(--spacing-sm)' }}>
                Premium Membership
              </div>
              <div style={{
                fontSize: '3rem',
                fontWeight: '700',
                color: 'var(--gold)',
                marginBottom: 'var(--spacing-sm)'
              }}>
                $2.99<span style={{ fontSize: '1.2rem', fontWeight: '400', color: 'var(--white-dim)' }}>/month</span>
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--white-dim)' }}>
                ✓ Custom vanity URL<br/>
                ✓ Unlimited links<br/>
                ✓ Real-time updates<br/>
                ✓ Premium themes
              </div>
            </div>
            
            <button
              onClick={() => window.location.href = '/signup'}
              className="dashboard-btn dashboard-btn-primary"
              style={{
                padding: 'var(--spacing-md) var(--spacing-xl)',
                fontSize: '1.1rem',
                width: '100%',
                maxWidth: '400px'
              }}
            >
              Complete Payment
            </button>
            
            <button
              onClick={handleLogout}
              className="dashboard-btn dashboard-btn-secondary"
              style={{
                marginTop: 'var(--spacing-md)',
                padding: 'var(--spacing-sm) var(--spacing-lg)',
                fontSize: '0.9rem'
              }}
            >
              Logout
            </button>
          </div>
        ) : (
          <>
        {/* Two Column Grid: Editor + Preview */}
        <div className="dashboard-grid">
          {/* Left: Editor */}
          <div className="dashboard-editor">
            {/* Profile Section */}
            <div className="dashboard-section">
              <h2 className="dashboard-section-title">
                <span className="dashboard-section-icon">👤</span>
                Profile
              </h2>

              <div className="dashboard-form-grid">
                <div className="dashboard-form-group">
                  <label className="dashboard-label">Display Name</label>
                  <input
                    type="text"
                    className="dashboard-input"
                    placeholder="John Doe"
                    value={profile.displayName}
                    onChange={(e) => handleProfileChange('displayName', e.target.value)}
                  />
                </div>

                <div className="dashboard-form-group">
                  <label className="dashboard-label">Bio</label>
                  <textarea
                    className="dashboard-textarea"
                    placeholder="Tell people about yourself..."
                    value={profile.bio}
                    onChange={(e) => handleProfileChange('bio', e.target.value)}
                  />
                </div>

                <div className="dashboard-form-group">
                  <label className="dashboard-label">Photo URL</label>
                  <input
                    type="url"
                    className="dashboard-input"
                    placeholder="https://example.com/your-photo.jpg"
                    value={profile.avatarUrl}
                    onChange={(e) => handleProfileChange('avatarUrl', e.target.value)}
                  />
                  <p style={{ 
                    fontSize: '0.8rem', 
                    color: 'var(--white-dim)', 
                    marginTop: '4px' 
                  }}>
                    Paste a URL to your profile photo (Instagram, LinkedIn, etc.)
                  </p>
                </div>

                <div className="dashboard-form-row">
                  <div className="dashboard-form-group">
                    <label className="dashboard-label">Public Email</label>
                    <input
                      type="email"
                      className="dashboard-input"
                      placeholder="you@example.com"
                      value={profile.email}
                      onChange={(e) => handleProfileChange('email', e.target.value)}
                    />
                  </div>

                  <div className="dashboard-form-group">
                    <label className="dashboard-label">Public Phone</label>
                    <input
                      type="tel"
                      className="dashboard-input"
                      placeholder="+1 (555) 123-4567"
                      value={profile.phone}
                      onChange={(e) => handleProfileChange('phone', e.target.value)}
                    />
                  </div>
                </div>

                <div className="dashboard-form-row">
                  <div className="dashboard-form-group">
                    <label className="dashboard-label">Card Glow Color</label>
                    <div className="dashboard-color-picker-row">
                      <input
                        type="text"
                        className="dashboard-input"
                        placeholder="#d4af37"
                        value={profile.glowColor}
                        onChange={(e) => handleProfileChange('glowColor', e.target.value)}
                      />
                      <input
                        type="color"
                        className="dashboard-color-input"
                        value={profile.glowColor}
                        onChange={(e) => handleProfileChange('glowColor', e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="dashboard-form-group">
                    <label className="dashboard-label">
                      Glow Intensity: {Math.round(profile.glowIntensity * 100)}%
                    </label>
                    <input
                      type="range"
                      className="dashboard-slider"
                      min="0"
                      max="1"
                      step="0.1"
                      value={profile.glowIntensity}
                      onChange={(e) => handleProfileChange('glowIntensity', parseFloat(e.target.value))}
                    />
                  </div>
                </div>

                <div className="dashboard-form-group">
                  <label className="dashboard-label">Card Font Style</label>
                  <select
                    className="dashboard-input"
                    value={profile.cardFont}
                    onChange={(e) => handleProfileChange('cardFont', e.target.value)}
                  >
                    <option value="playfair">Playfair Display (Classic Serif)</option>
                    <option value="inter">Inter (Modern Sans)</option>
                    <option value="montserrat">Montserrat (Bold Sans)</option>
                    <option value="bebas">Bebas Neue (Display)</option>
                    <option value="cinzel">Cinzel (Elegant Serif)</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Links Section */}
            <div className="dashboard-section">
              <h2 className="dashboard-section-title">
                <span className="dashboard-section-icon">🔗</span>
                Links
              </h2>

              <div className="dashboard-links-container">
                <div className="dashboard-links-list">
                  {links.length === 0 ? (
                    <div className="dashboard-empty-state">
                      <div className="dashboard-empty-icon">🔗</div>
                      <p>No links yet. Add your first link below!</p>
                    </div>
                  ) : (
                    links.map((link, index) => (
                      <div key={link.id} className="dashboard-link-item">
                        <div className="dashboard-link-info">
                          <div className="dashboard-link-title">{link.title}</div>
                          <div className="dashboard-link-url">{link.url}</div>
                        </div>
                        <div className="dashboard-link-controls">
                          <button
                            className="dashboard-icon-btn"
                            onClick={() => handleMoveLink(index, 'up')}
                            disabled={index === 0}
                            title="Move up"
                          >
                            ↑
                          </button>
                          <button
                            className="dashboard-icon-btn"
                            onClick={() => handleMoveLink(index, 'down')}
                            disabled={index === links.length - 1}
                            title="Move down"
                          >
                            ↓
                          </button>
                          <button
                            className="dashboard-icon-btn dashboard-icon-btn-danger"
                            onClick={() => handleDeleteLink(link.id)}
                            title="Delete"
                          >
                            🗑
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="dashboard-add-link-form">
                  <div className="dashboard-add-link-inputs">
                    <input
                      type="text"
                      className="dashboard-input"
                      placeholder="Link Title (e.g., LinkedIn)"
                      value={newLink.title}
                      onChange={(e) => setNewLink({ ...newLink, title: e.target.value })}
                    />
                    <input
                      type="url"
                      className="dashboard-input"
                      placeholder="URL (e.g., https://linkedin.com/in/yourname)"
                      value={newLink.url}
                      onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                    />
                  </div>
                  <button
                    className="dashboard-btn dashboard-btn-primary"
                    onClick={handleAddLink}
                    disabled={!newLink.title || !newLink.url}
                    style={{ width: '100%' }}
                  >
                    ➕ Add Link
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Live Preview */}
          <div className="dashboard-preview-wrapper">
            <div className="dashboard-preview">
              <div className="dashboard-preview-label">
                👁 Live Preview
              </div>
              
              <div 
                className={`dashboard-card-preview-premium dashboard-card-font-${profile.cardFont}`}
                style={{
                  boxShadow: `0 0 ${60 * profile.glowIntensity}px ${profile.glowColor}${Math.round(profile.glowIntensity * 120).toString(16).padStart(2, '0')}`,
                  border: `2px solid ${profile.glowColor}${Math.round(profile.glowIntensity * 80).toString(16).padStart(2, '0')}`
                }}
              >
                {/* Profile Photo */}
                <div className="dashboard-card-photo-container">
                  {profile.avatarUrl ? (
                    <img 
                      src={profile.avatarUrl} 
                      alt={profile.displayName || 'Profile'} 
                      className="dashboard-card-photo"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className="dashboard-card-photo-placeholder"
                    style={{ 
                      display: profile.avatarUrl ? 'none' : 'flex',
                      background: `linear-gradient(135deg, ${profile.glowColor}, ${profile.glowColor}dd)`
                    }}
                  >
                    {profile.displayName ? profile.displayName.charAt(0).toUpperCase() : '👤'}
                  </div>
                </div>

                {/* Name */}
                <h3 className="dashboard-card-name-premium">
                  {profile.displayName || 'YOUR NAME'}
                </h3>

                {/* Bio */}
                {profile.bio && (
                  <p className="dashboard-card-bio-premium">
                    {profile.bio}
                  </p>
                )}

                {/* Contact & Links - All Inside Card */}
                <div className="dashboard-card-actions">
                  {/* Contact Buttons */}
                  {profile.email && (
                    <a
                      href={`mailto:${profile.email}`}
                      className="dashboard-card-action-btn"
                      style={{
                        background: `linear-gradient(135deg, ${profile.glowColor}, ${profile.glowColor}dd)`,
                        boxShadow: `0 4px 16px ${profile.glowColor}40`
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      EMAIL {profile.displayName ? profile.displayName.toUpperCase().split(' ')[0] : 'ME'}
                    </a>
                  )}

                  {profile.phone && (
                    <a
                      href={`tel:${profile.phone}`}
                      className="dashboard-card-action-btn"
                      style={{
                        background: `linear-gradient(135deg, ${profile.glowColor}, ${profile.glowColor}dd)`,
                        boxShadow: `0 4px 16px ${profile.glowColor}40`
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      CALL {profile.displayName ? profile.displayName.toUpperCase().split(' ')[0] : 'ME'}
                    </a>
                  )}

                  {/* Links */}
                  {links.map(link => (
                    <a
                      key={link.id}
                      href={link.url}
                      className="dashboard-card-action-btn"
                      style={{
                        background: `linear-gradient(135deg, ${profile.glowColor}, ${profile.glowColor}dd)`,
                        boxShadow: `0 4px 16px ${profile.glowColor}40`
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link.title.toUpperCase()}
                    </a>
                  ))}

                  {/* Empty State */}
                  {links.length === 0 && !profile.email && !profile.phone && (
                    <div className="dashboard-card-empty">
                      Add contact info or links to see buttons here
                    </div>
                  )}
                </div>

                {/* Share URL */}
                <div className="dashboard-card-share">
                  Share this link: <span className="dashboard-card-url">risemyrank.club/{profile.slug}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
